import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
} from "@mui/material";
import { LocalHospital, Bed } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import AdmissionsCardSkeleton from "../../../../Components/GeneralComponents/admissionsCardSkeleton/AdmissionsCardSkeleton";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";

const ActiveAdmissions = ({ patientId }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Fetch active IPD admissions (status=1 means admitted)
  const {
    data: ipdData,
    isLoading: ipdLoading,
    isError: ipdError,
  } = useFetchObjects(
    patientId ? ["ipd-admissions", patientId, "active"] : null,
    patientId ? `ipd-admissions/?patient=${patientId}&status=1` : "",
    token
  );

  // Fetch active ICU admissions (status=1 means admitted)
  const {
    data: icuData,
    isLoading: icuLoading,
    isError: icuError,
  } = useFetchObjects(
    patientId ? ["icu-admissions", patientId, "active"] : null,
    patientId ? `icu-admissions/?patient=${patientId}&status=1` : "",
    token
  );

  const ipdAdmissions = ipdData?.results || [];
  const icuAdmissions = icuData?.results || [];

  if (ipdLoading || icuLoading) {
    return <AdmissionsCardSkeleton count={2} />;
  }

  if (ipdError || icuError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t("profile.errorLoadingAdmissions") ||
          "Error loading admissions data"}
      </Alert>
    );
  }

  if (ipdAdmissions.length === 0 && icuAdmissions.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("profile.noActiveAdmissions") || "No active admissions"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t("profile.activeAdmissions") || "Active Admissions"}
      </Typography>
      <Grid container spacing={2}>
        {/* IPD Admissions */}
        {ipdAdmissions.map((admission) => (
          <Grid item xs={12} key={admission.id}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalHospital color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        IPD Admission
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {admission.ward_name || admission.bed?.room?.ward?.name || "N/A"} - Room{" "}
                        {admission.bed?.room?.room_number || "N/A"} - Bed{" "}
                        {admission.bed?.bed_number || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("profile.admittedOn") || "Admitted on"}:{" "}
                        {admission.admission_date
                          ? new Date(admission.admission_date).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                      {(admission.admitting_doctor_name ||
                        (admission.admitting_doctor &&
                          typeof admission.admitting_doctor === "object")) && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.doctor") || "Doctor"}:{" "}
                          {admission.admitting_doctor_name ||
                            (admission.admitting_doctor?.first_name &&
                              `${admission.admitting_doctor.first_name} ${admission.admitting_doctor.last_name || ""}`.trim()) ||
                            (typeof admission.admitting_doctor === "number"
                              ? `ID: ${admission.admitting_doctor}`
                              : "N/A")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Chip
                    label={
                      admission.status === 1
                        ? "Admitted"
                        : admission.status === 2
                        ? "Discharged"
                        : admission.status === 3
                        ? "Transferred"
                        : "Unknown"
                    }
                    color="success"
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* ICU Admissions */}
        {icuAdmissions.map((admission) => (
          <Grid item xs={12} key={admission.id}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Bed color="error" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        ICU Admission
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {admission.bed_info || `Bed ${admission.bed?.bed_number || "N/A"}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("profile.admittedOn") || "Admitted on"}:{" "}
                        {admission.admission_date
                          ? new Date(admission.admission_date).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                      {(admission.admitting_doctor_name ||
                        (admission.admitting_doctor &&
                          typeof admission.admitting_doctor === "object")) && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.doctor") || "Doctor"}:{" "}
                          {admission.admitting_doctor_name ||
                            (admission.admitting_doctor?.first_name &&
                              `${admission.admitting_doctor.first_name} ${admission.admitting_doctor.last_name || ""}`.trim()) ||
                            (typeof admission.admitting_doctor === "number"
                              ? `ID: ${admission.admitting_doctor}`
                              : "N/A")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Chip
                    label={
                      admission.status === 1
                        ? "Admitted"
                        : admission.status === 2
                        ? "Discharged"
                        : admission.status === 3
                        ? "Transferred"
                        : "Unknown"
                    }
                    color="error"
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ActiveAdmissions;
